import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { CircularProgress } from '@mui/material';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useSharedAccessAuth } from 'apps/sigma2-ui/src/lib/hooks/useSharedAccessAuth';
import { SharedAccessResourceType } from 'libs/end-to-end/src/auth';
import { guardExhaustive } from 'libs/end-to-end/src/generic';

type Props = {
  resourceId: string;
  resourceType: SharedAccessResourceType;
  publicPath: (resourceId: string) => string;
  sharedPath: (resourceId: string) => string;
  fallbackPath?: string;
  authenticatedComponent: React.ReactNode;
};

export const AccessGate: React.FC<Props> = ({
  resourceId,
  resourceType,
  publicPath,
  sharedPath,
  fallbackPath = '/',
  authenticatedComponent,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, myUserStatus } = useUser();
  const { status, checkAccess } = useSharedAccessAuth();

  const isAuthResolved = myUserStatus !== null && myUserStatus !== 'logging_in';

  useEffect(() => {
    if (!resourceId) return;
    if (!isAuthResolved) return;
    if (isAuthenticated) return;

    checkAccess({
      resourceId,
      resourceType,
    });
  }, [checkAccess, resourceId, resourceType, isAuthenticated, isAuthResolved]);

  useEffect(() => {
    if (!resourceId) return;
    if (!isAuthResolved) return;
    if (isAuthenticated) return;

    if (status.state === 'access-checked') {
      if (!status.hasAccess) {
        if (status.accessType === 'shared') {
          navigate(sharedPath(resourceId));
          return;
        }

        navigate(fallbackPath);
        return;
      }

      switch (status.accessType) {
        case 'public':
          navigate(publicPath(resourceId));
          return;
        case 'shared':
          navigate(sharedPath(resourceId));
          return;
        case 'user':
          return;
        case 'none':
          navigate(fallbackPath);
          return;
        default:
          throw guardExhaustive(status.accessType);
      }
    }

    if (status.state === 'access-check-failed') {
      navigate(fallbackPath);
    }
  }, [
    status,
    resourceId,
    resourceType,
    isAuthenticated,
    navigate,
    publicPath,
    sharedPath,
    fallbackPath,
    isAuthResolved,
  ]);

  if (!isAuthResolved) {
    return null;
  }

  if (isAuthenticated) {
    return <>{authenticatedComponent}</>;
  }

  if (status.state === 'checking-access' || status.state === 'idle') {
    return <CircularProgress size={40} />;
  }

  return null;
};
